/**
 * A short burst of confetti, for the one moment this product celebrates: a recipient opening a
 * certificate that is waiting for them.
 *
 * It is drawn on a `<canvas>` that lies over the page and takes no input — `pointer-events: none`,
 * `aria-hidden`, no focusable element and nothing in the accessibility tree — so it cannot cover a
 * word, swallow a click or catch a keystroke. It runs for a fixed couple of seconds, cancels its
 * own animation frame, removes its own canvas and revokes nothing because it holds nothing.
 *
 * WHAT IT REFUSES TO DO
 * ---------------------
 * It does not run when the viewer asked for less motion (`prefers-reduced-motion: reduce`); that
 * is a refusal, not a shorter animation. It does not run twice for the same record in the same tab
 * — a re-render, a dialog opening, a language switch or a back-and-forward is not a new
 * certificate. And it never runs on an invalid, expired, already-used, declined or failed link:
 * the caller decides that, and the caller is the one screen that knows it is showing good news.
 *
 * No timers are left behind, no listener is registered on `window`, and nothing is written to
 * storage: «already celebrated» is a module-level set, which is this tab's memory and nothing else.
 */

/** How long the burst lasts, in milliseconds. Short on purpose: a greeting, not an interruption. */
const DURATION_MS = 2400;
const PIECES = 90;
/** The product's own three, plus the two neutrals the page already uses. */
const COLOURS = ['#CF051E', '#F8BA3F', '#1E8A4C', '#0A66C2', '#111111'] as const;

/** Records celebrated in THIS tab. Memory only — never `localStorage`, never a cookie. */
const celebrated = new Set<string>();

/** Tests only: no product path replays a celebration. */
export function forgetCelebrations(): void {
  celebrated.clear();
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    /* A browser that cannot answer is not a browser that said yes. */
    return true;
  }
}

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  spin: number;
  angle: number;
  colour: string;
}

/**
 * Celebrates once, for `key`. Returns a function that stops it early — the caller's unmount.
 *
 * Answers a no-op cleanup, and draws nothing, when the viewer asked for less motion, when this
 * record has already been celebrated in this tab, or when there is no canvas to draw on.
 */
export function celebrate(key: string): () => void {
  const noop = (): void => undefined;
  if (typeof document === 'undefined' || typeof window === 'undefined') return noop;
  if (celebrated.has(key)) return noop;
  if (reducedMotion()) {
    /* Marked anyway: the decision was made for this record, and a later render must not re-ask. */
    celebrated.add(key);
    return noop;
  }
  celebrated.add(key);

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return noop;

  const width = window.innerWidth || 1024;
  const height = window.innerHeight || 768;
  canvas.width = width;
  canvas.height = height;
  canvas.setAttribute('aria-hidden', 'true');
  canvas.dataset.hpwConfetti = '1';
  canvas.style.cssText = [
    'position:fixed',
    'inset:0',
    'width:100%',
    'height:100%',
    'pointer-events:none',
    'z-index:60',
  ].join(';');
  document.body.appendChild(canvas);

  const pieces: Piece[] = Array.from({ length: PIECES }, (): Piece => {
    const colour = COLOURS[Math.floor(Math.random() * COLOURS.length)] as string;
    return {
      x: Math.random() * width,
      y: -20 - Math.random() * height * 0.5,
      vx: (Math.random() - 0.5) * 60,
      vy: 120 + Math.random() * 180,
      size: 6 + Math.random() * 6,
      spin: (Math.random() - 0.5) * 8,
      angle: Math.random() * Math.PI,
      colour,
    };
  });

  let frame = 0;
  let last = 0;
  const started = Date.now();
  let stopped = false;

  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    if (frame) cancelAnimationFrame(frame);
    canvas.remove();
  };

  const draw = (now: number): void => {
    if (stopped) return;
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.016;
    last = now;
    const elapsed = Date.now() - started;
    if (elapsed >= DURATION_MS) {
      stop();
      return;
    }
    /* Fades out over the last third, so it leaves rather than disappearing. */
    context.clearRect(0, 0, width, height);
    context.globalAlpha =
      elapsed > DURATION_MS * 0.66 ? 1 - (elapsed - DURATION_MS * 0.66) / (DURATION_MS * 0.34) : 1;
    for (const p of pieces) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.angle += p.spin * dt;
      context.save();
      context.translate(p.x, p.y);
      context.rotate(p.angle);
      context.fillStyle = p.colour;
      context.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      context.restore();
    }
    frame = requestAnimationFrame(draw);
  };
  frame = requestAnimationFrame(draw);
  return stop;
}
